import { useState } from "react";
import { formatCurrency } from "../../../utils/formatters";
import { useRequestPaymentDeleteMutation } from "../api/billingApi";

interface Props {
  payment: any;
  onClose: () => void;
  onSuccess: () => void;
}

export default function RequestPaymentDeleteModal({ payment, onClose, onSuccess }: Props) {
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");

  const [requestDelete, { isLoading }] = useRequestPaymentDeleteMutation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reason.trim()) {
      setError("Reason for deletion is required.");
      return;
    }
    setError("");
    try {
      await requestDelete({ id: payment.id, body: { reason: reason.trim() } }).unwrap();
      onSuccess();
    } catch (err: any) {
      setError(err?.data?.message || "Failed to submit delete request. Try again.");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-slate-100">
          <div>
            <h2 className="text-base font-black text-rose-700 uppercase tracking-widest">
              Request Payment Deletion
            </h2>
            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-0.5">
              This will require a second admin to approve
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 transition-colors text-xl leading-none"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          {/* Payment summary */}
          <div className="bg-rose-50 border border-rose-100 rounded-2xl p-4 space-y-2">
            <p className="text-[9px] font-black text-rose-400 uppercase tracking-widest mb-2">
              Payment to be deleted
            </p>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div>
                <span className="text-slate-400 font-bold uppercase tracking-widest text-[9px] block">Receipt</span>
                <span className="font-black text-slate-800">{payment.receipt_number || "N/A"}</span>
              </div>
              <div>
                <span className="text-slate-400 font-bold uppercase tracking-widest text-[9px] block">Amount</span>
                <span className="font-black text-slate-800">{formatCurrency(payment.amount)}</span>
              </div>
              <div>
                <span className="text-slate-400 font-bold uppercase tracking-widest text-[9px] block">Member</span>
                <span className="font-black text-slate-800">{payment.user?.name || "—"}</span>
              </div>
              <div>
                <span className="text-slate-400 font-bold uppercase tracking-widest text-[9px] block">Method</span>
                <span className="font-black text-slate-800 capitalize">{payment.payment_method || "—"}</span>
              </div>
              <div>
                <span className="text-slate-400 font-bold uppercase tracking-widest text-[9px] block">Date Paid</span>
                <span className="font-black text-slate-800">
                  {payment.paid_at
                    ? new Date(payment.paid_at).toLocaleDateString("en-NG", { day: "2-digit", month: "short", year: "numeric" })
                    : "—"}
                </span>
              </div>
              <div>
                <span className="text-slate-400 font-bold uppercase tracking-widest text-[9px] block">Reference</span>
                <span className="font-mono text-slate-700 text-[10px]">{payment.reference || "—"}</span>
              </div>
            </div>
          </div>

          {/* Warning */}
          <div className="flex items-start gap-2 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2">
            <svg className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
            <p className="text-[10px] font-bold text-amber-700">
              Deletion is irreversible once approved. A second admin must approve this request before the payment is removed.
            </p>
          </div>

          {/* Reason */}
          <div>
            <label className="text-[10px] font-black text-slate-700 uppercase tracking-widest block mb-1">
              Reason for Deletion <span className="text-rose-500">*</span>
            </label>
            <textarea
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Explain why this payment should be deleted..."
              className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm text-slate-800 bg-slate-50 outline-none focus:ring-2 ring-rose-100 resize-none"
            />
          </div>

          {error && (
            <p className="text-xs font-bold text-rose-600 bg-rose-50 border border-rose-100 rounded-xl px-3 py-2">
              {error}
            </p>
          )}

          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 text-[10px] font-black uppercase tracking-widest rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 py-2.5 text-[10px] font-black uppercase tracking-widest rounded-xl bg-rose-600 text-white hover:bg-rose-700 transition-all disabled:opacity-50"
            >
              {isLoading ? "Submitting..." : "Submit Delete Request"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
